// @ts-strict-ignore
import qs from 'qs';
import { useRouter } from 'next/router';
import { CuratorOptions } from 'api';
import { FilterTopic } from 'pages/playlists';

const BASE_PATH = `/playlists`;

type PlaylistSearchHelpers = {
  addTopic: (topic: FilterTopic) => void;
  removeTopic: (topic: string) => void;
  setCurator: (curator: CuratorOptions | null) => void;
  clearAll: () => void;
  setActivePage: (index: number) => void;
  getCurrentQueryParams: () => {
    topics: string[];
    curator: string[];
    page: string;
  };
};

export default function usePlaylistSearch(
  setModalOpen: (isOpen: boolean) => void
): PlaylistSearchHelpers {
  const router = useRouter();

  const getCurrentQueryParams = () => {
    let currentQueryParams;

    if (typeof window !== 'undefined') {
      currentQueryParams = qs.parse(window.location.search, {
        ignoreQueryPrefix: true
      });
    }

    return currentQueryParams;
  };

  const createQueryString = (topics, curator, page) => {
    const queryString = qs.stringify(
      { curator, topics, page },
      { addQueryPrefix: true, skipNulls: true, arrayFormat: 'brackets' }
    );

    return queryString;
  };

  const goToPath = (queryString: string) => {
    router.push(`${BASE_PATH}${queryString}`, undefined, {
      shallow: true,
      scroll: false
    });
  };

  const setActivePage = (page: number) => {
    const queryString = createQueryString(
      getCurrentQueryParams().topics,
      getCurrentQueryParams().curator,
      page
    );

    goToPath(queryString);
  };

  const setCurator = (curator: string) => {
    const currentCurator = getCurrentQueryParams().curator as string;
    const isSameCurator =
      currentCurator?.toLowerCase() === curator?.toLowerCase();

    const newCuratorParam = isSameCurator ? null : curator;

    const queryString = createQueryString(
      getCurrentQueryParams().topics,
      newCuratorParam,
      1
    );

    goToPath(queryString);
  };

  const addTopic = (topic: FilterTopic) => {
    const currentTopics = (getCurrentQueryParams().topics as string[]) || [];
    const currentTopicsLowercase = currentTopics.map(topic =>
      topic.toLowerCase()
    );
    const isTopicAlreadyIncluded = currentTopicsLowercase?.includes(
      topic.name.toLowerCase()
    );

    if (isTopicAlreadyIncluded) {
      removeTopic(topic.name);
      setModalOpen(false);
      return;
    }

    const newTopics = isTopicAlreadyIncluded
      ? [topic.name]
      : [...currentTopics, topic.name];

    const queryString = createQueryString(
      newTopics,
      getCurrentQueryParams().curator,
      1
    );

    setModalOpen(false);

    goToPath(queryString);
  };

  const removeTopic = (topic: string) => {
    const currentTopics = getCurrentQueryParams().topics as string[];
    const currentTopicsLowercase = currentTopics.map(topic =>
      topic.toLowerCase()
    );
    const removedTopicIndex = currentTopicsLowercase.indexOf(
      topic.toLowerCase()
    );

    const newTopics = [...currentTopicsLowercase];
    newTopics.splice(removedTopicIndex, 1);

    const queryString = createQueryString(
      newTopics,
      getCurrentQueryParams().curator,
      1
    );

    goToPath(queryString);
  };

  const clearAll = () => {
    const queryString = createQueryString(null, null, 1);

    goToPath(queryString);
  };

  return {
    getCurrentQueryParams,
    setActivePage,
    addTopic,
    setCurator,
    removeTopic,
    clearAll
  };
}
